import { useEffect, useState, useCallback, useMemo } from "react";
import permissionsApi from "../services/permissionsApi";

// ---------------------------------------------------------------------------
// usePermissions — React hook for the current user's full access bundle.
//
// Usage:
//   const { hasAccess, canAccessModule, role, roleId, isManager,
//           isSuperUser, epoLimit, isLoading, refresh } = usePermissions();
//
//   if (hasAccess(PERM.SUPER_USER))        { ... }
//   if (hasAccess("HR Menu"))              { ... }   // also OK, case-insensitive
//   if (canAccessModule("epo.accounting")) { ... }
//
// Returns:
//   bundle           — full EffectiveAccessBundle (or null)
//   hasAccess        — case-insensitive permission name lookup
//   canAccessModule  — module-key lookup ("dept.hr", "dms.release", ...)
//   role             — role name string
//   roleId           — int role id from EmpBasic.RoleId
//   isManager        — server-resolved manager flag (RoleId OR real supervisor)
//   isSuperUser      — server-resolved super-user flag (perm OR legacy override)
//   isItAdmin        — RoleId == 4
//   epoLimit         — decimal from empbasic.number01 (NULL → null)
//   country          — "USA" / "INDIA" / "JOSEPH"
//   department       — JCDept
//   isLoading        — true during initial fetch
//   error            — set if a refresh fails
//   refresh()        — re-fetches /api/Permissions/me
//
// Cache:
//   The login thunk writes the bundle into sessionStorage["permissions"].
//   On mount, this hook reads from cache first. If the cache is empty or
//   not a bundle (legacy: just an array), it fetches /api/Permissions/me.
// ---------------------------------------------------------------------------

/** Returns true if `v` looks like a bundle object (not just the legacy array). */
function isBundleShape(v) {
    return v && !Array.isArray(v) && typeof v === "object";
}

export function usePermissions() {
    const initial = permissionsApi.readCached();
    const [bundle, setBundle] = useState(isBundleShape(initial) ? initial : null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Mount-only effect: if we don't have a usable bundle, fetch it.
    // Reading sessionStorage inside the effect (rather than depending on
    // bundle state) keeps the deps array honest and avoids infinite
    // re-fetch loops.
    useEffect(() => {
        let alive = true;
        const cached = permissionsApi.readCached();
        if (isBundleShape(cached)) return undefined;

        setIsLoading(true);
        permissionsApi
            .refresh()
            .then((b) => { if (alive) setBundle(b); })
            .catch((e) => { if (alive) setError(e); })
            .finally(() => { if (alive) setIsLoading(false); });

        return () => { alive = false; };
    }, []);

    const refresh = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const b = await permissionsApi.refresh();
            setBundle(b);
            return b;
        } catch (e) {
            setError(e);
            throw e;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Pre-lowered set for O(1) case-insensitive permission lookups.
    const permsSet = useMemo(() => {
        const arr = bundle?.permissions;
        if (!Array.isArray(arr)) return null;
        const s = new Set();
        for (const p of arr) if (typeof p === "string") s.add(p.toLowerCase());
        return s;
    }, [bundle]);

    const hasAccess = useCallback(
        (perm) => {
            if (!perm || !permsSet) return false;
            return permsSet.has(String(perm).toLowerCase());
        },
        [permsSet]
    );

    const canAccessModule = useCallback(
        (moduleKey) => {
            if (!moduleKey || !bundle?.modules) return false;
            return bundle.modules[moduleKey] === true;
        },
        [bundle]
    );

    return {
        bundle,

        // helpers
        hasAccess,
        canAccessModule,

        // identity
        role:         bundle?.role        ?? null,
        roleId:       bundle?.roleId      ?? null,
        epoLimit:     bundle?.epoLimit    ?? null,
        department:   bundle?.department  ?? null,
        country:      bundle?.country     ?? null,
        companyCode:  bundle?.companyCode ?? null,
        supervisorId: bundle?.supervisorId ?? null,

        // resolved flags
        isManager:     !!bundle?.isManager,
        isActualManager: !!bundle?.isActualManager,
        isSuperUser:   !!bundle?.isSuperUser,
        isItAdmin:     !!bundle?.isItAdmin,
        isPresident:   !!bundle?.isPresident,
        isHr:          !!bundle?.isHr,
        isFinance:     !!bundle?.isFinance,
        isDmsManager:  !!bundle?.isDmsManager,
        isSalvie:      !!bundle?.isSalvie,
        isSheryl:      !!bundle?.isSheryl,
        isBabu:        !!bundle?.isBabu,
        isMpc:         !!bundle?.isMpc,
        isShipper:     !!bundle?.isShipper,
        isTaskAuditor: !!bundle?.isTaskAuditor,
        isMaintenanceTeam: !!bundle?.isMaintenanceTeam,
        isBetaTester:  !!bundle?.isBetaTester,
        isSnSignatory: !!bundle?.isSnSignatory,
        canViewIndia:  !!bundle?.canViewIndia,
        canViewUsa:    !!bundle?.canViewUsa,

        isLoading,
        error,
        refresh,
    };
}

export default usePermissions;
